import { getSharedScheduleErrorMessage } from '../sharedMembership/errors';
import {
    QuickMoveErrorNotice,
    QuickMoveFailure,
    QuickMoveKind,
    QuickMoveRefetchState,
    QuickMoveSaveNotice,
    QuickMoveVerification,
} from './types';

export const SCHEDULE_STALE_ERROR_CODE = 'SCHEDULE_STALE';

const readErrorCode = (failure: QuickMoveFailure): string => {
    if (typeof failure.errorCode === 'string' && failure.errorCode.trim()) return failure.errorCode.trim();
    const data = failure.data as { errorCode?: unknown } | null | undefined;
    return data && typeof data.errorCode === 'string' ? data.errorCode.trim() : '';
};

const readServerMessage = (failure: QuickMoveFailure): string => {
    if (typeof failure.message === 'string' && failure.message.trim()) return failure.message.trim();
    const data = failure.data as { message?: unknown } | null | undefined;
    return data && typeof data.message === 'string' ? data.message.trim() : '';
};

/**
 * 저장 요청 실패 → 문구(§3·§6-4). **재조회 전** 판정이다 — 최종 문구는 buildQuickMoveSaveNotice 가
 * 재조회 결과까지 합쳐 정한다(일반일정은 저장 후 오류가 날 수 있다).
 *
 * 우선순위: 결과 미확인(0·5xx) → SCHEDULE_STALE → 공유 수강권 오류(기존 문구 재사용) → 상태 코드.
 * 무응답(0)·5xx 는 결과 미확인으로 본다 — PT 교체는 생성→취소 사이에서 끊기면 둘 다 남을 수 있다(D4).
 */
export const resolveQuickMoveError = (failure: QuickMoveFailure, kind: QuickMoveKind): QuickMoveErrorNotice => {
    const status = typeof failure.status === 'number' ? failure.status : 0;
    const errorCode = readErrorCode(failure);

    if (status === 0 || status >= 500) {
        return {
            code: 'OUTCOME_UNKNOWN',
            title: '결과를 확인하지 못했어요',
            message: '저장됐는지 확인하지 못했어요. 일정을 새로고침해 실제로 바뀌었는지 확인해주세요.',
            outcomeUnknown: true,
        };
    }

    if (errorCode === SCHEDULE_STALE_ERROR_CODE) {
        return {
            code: 'STALE',
            title: '다른 곳에서 바뀌었어요',
            message: '이 일정이 다른 화면에서 먼저 바뀌었어요. 최신 일정을 확인한 뒤 다시 옮겨주세요.',
            outcomeUnknown: false,
        };
    }

    const sharedMessage = getSharedScheduleErrorMessage({ status, data: failure.data });
    if (sharedMessage) {
        return { code: 'SHARED_MEMBERSHIP', title: '일정을 바꾸지 못했어요', message: sharedMessage, outcomeUnknown: false };
    }

    if (status === 404) {
        // 단정하지 않는다 — 일반일정은 저장 후 강사 조회 단계에서도 404 가 날 수 있다.
        return {
            code: 'NOT_FOUND',
            title: '일정을 찾지 못했어요',
            message: '이미 변경되었거나 삭제되었을 수 있어요. 최신 일정을 확인해주세요.',
            outcomeUnknown: false,
        };
    }

    if (status === 410 || status === 420) {
        return {
            code: 'TIME_CONFLICT',
            title: '강사 일정이 겹쳐요',
            message: '옮기려는 시간에 강사님의 다른 일정이 있어요. 다른 시간을 선택해주세요.',
            outcomeUnknown: false,
        };
    }

    if (status === 430) {
        return {
            code: 'PENDING_REQUEST',
            title: '변경 신청이 대기 중이에요',
            message: '회원의 변경 신청이 대기 중인 시간이에요. 신청을 먼저 처리하거나 다른 시간을 선택해주세요.',
            outcomeUnknown: false,
        };
    }

    const serverMessage = readServerMessage(failure);
    if (status === 401 || status === 403) {
        return {
            code: 'FORBIDDEN',
            title: '일정을 바꿀 수 없어요',
            message: serverMessage || '이 일정을 바꿀 권한이 없어요.',
            outcomeUnknown: false,
        };
    }
    if (status === 400) {
        return {
            code: 'INVALID_INPUT',
            title: '일정을 바꾸지 못했어요',
            message: serverMessage || '입력한 날짜와 시간을 다시 확인해주세요.',
            outcomeUnknown: false,
        };
    }

    return {
        code: 'OTHER',
        title: '일정을 바꾸지 못했어요',
        message: serverMessage || (kind === 'private' ? '수업 시간을 바꾸지 못했어요.' : '일정 시간을 바꾸지 못했어요.'),
        outcomeUnknown: false,
    };
};

const UNCONFIRMED_NOTICE: QuickMoveSaveNotice = {
    tone: 'warning',
    title: '결과를 확인하지 못했어요',
    message: '최신 일정을 불러오지 못했어요. 새로고침해 실제로 바뀌었는지 확인해주세요.',
    showRefresh: true,
};

/**
 * 저장 + 재조회 결과 → 화면에 띄울 최종 문구.
 * 성공·실패 모두 재조회 후 **실제 상태**로 문구를 정한다(§3·§6-4).
 */
export const buildQuickMoveSaveNotice = ({
    requestOk,
    error,
    refetch,
    verification,
}: {
    requestOk: boolean;
    error?: QuickMoveErrorNotice;
    refetch: QuickMoveRefetchState;
    verification: QuickMoveVerification;
}): QuickMoveSaveNotice => {
    if (verification === 'bothPresent') {
        return {
            tone: 'warning',
            title: '원래 일정이 남아 있어요',
            message: '새 일정은 생겼지만 원래 일정이 아직 남아 있어요. 일정을 확인한 뒤 남은 일정을 정리해주세요.',
            showRefresh: true,
        };
    }

    if (requestOk) {
        if (verification === 'applied' && refetch === 'ok') {
            return { tone: 'success', title: '일정을 옮겼어요', message: '변경한 시간으로 반영됐어요.', showRefresh: false };
        }
        return {
            ...UNCONFIRMED_NOTICE,
            message: '저장 요청은 완료됐지만 반영된 일정을 확인하지 못했어요. 새로고침해 확인해주세요.',
        };
    }

    if (verification === 'applied') {
        return {
            tone: 'warning',
            title: '일정은 옮겨졌어요',
            message: error?.outcomeUnknown
                ? '변경은 반영됐지만 처리 중 오류가 있었어요. 새로고침해 최신 일정을 확인해주세요.'
                : '변경은 반영됐지만 처리 중 오류가 있었어요. 화면의 일정을 확인해주세요.',
            showRefresh: !!error?.outcomeUnknown || refetch !== 'ok',
        };
    }

    if (error?.outcomeUnknown) {
        // 원본이 보여도 지연 처리·부분 교체가 끝났다고 확정할 수 없다.
        return { tone: 'warning', title: error.title, message: error.message, showRefresh: true };
    }

    if (verification === 'notApplied' && refetch === 'ok' && error) {
        return { tone: 'error', title: error.title, message: error.message, showRefresh: false };
    }

    if (refetch !== 'ok') {
        return UNCONFIRMED_NOTICE;
    }

    // 재조회는 됐지만 원본·목표 모두 보이지 않는 경우 등 — 요청 오류 문구 + 새로고침.
    return {
        tone: 'error',
        title: error?.title || '일정을 바꾸지 못했어요',
        message: error?.message || '일정을 바꾸지 못했어요.',
        showRefresh: true,
    };
};
